// ************** fronto.annotation.js **************
// concerns "generating" annotations (revised for Fronto)
// should be stand-alone (no dependency on the other JS files, but on vegaEmbed)

import * as d3 from 'd3';
import * as Fronto from "./utility.js";

export function frontoAnnotationGenerator(placeSelector, annoSpec, viewname, spaceGrid, isAuto, dblClick) {
  // annogen utility
  function mapMatched(map1, map2) {
    let mapIter = map1.entries();
    let score = 0;
    for (let i = 0; i < map1.size; i++) {
      let entry1 = mapIter.next().value;
      if (entry1[1] == map2.get(entry1[0])) score++;
    }
    return score;
  }
  function getMatchedDatumMark(selectedDatum, viewname) {
    // match the data item description & the actual mark
    let dataMarks = d3.selectAll(placeSelector + " #view-" + viewname + " svg g.role-mark *[aria-label]");
    let maxScore = 0,
      maxItem; // score based
    let maxByMark = true;
    dataMarks.each((d) => {
      let score = 0;
      let d_datum = new Map(Object.keys(d.datum).map((key) => [key, d.datum[key]]));
      let s_datum = new Map(Object.keys(selectedDatum.datum).map((key) => [key, selectedDatum.datum[key]]));
      let datum_match_score = mapMatched(d_datum, s_datum);
      score = datum_match_score;
      if (datum_match_score > 0) {
        maxByMark = false;
      }
      if (d.ariaRoleDescription === selectedDatum.role) score++; // mark type
      if (maxScore < score) {
        // updating max
        maxScore = score;
        maxItem = d;
      }
    });
    if (maxByMark) {
      // when the max score is only through matching mark --> undefined
      return undefined;
    }
    return maxItem;
  }
  function biFormat(text) {
    let biMatches = text.match(/(\$[a-z\-]+\$)/g);
    biMatches?.forEach(match => {
      text = text.replace(match, "<i class=\"bi bi-" + match.replace(/\$/g, '') + "\"></i>");
    })
    return text;
  }
  function biEscape(text) {
    let biMatches = text.match(/(\$[a-z\-]+\$)/g);
    biMatches?.forEach(match => {
      text.replace(match, "%");
    })
    return text;
  }
  // deals numbering counter
  let numberingCounter = 0;
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVW".split("");
  const alphabetLower = alphabet.map((d) => {
    return d.toLowerCase();
  });
  function getExternalizeNumber(style) {
    if (style === undefined || style === "number") {
      return ++numberingCounter;
    } else if (style === "alphLow") {
      return alphabetLower[numberingCounter++];
    } else if (style === "alphUp") {
      return alphabet[numberingCounter++];
    }
  }
  // color Control
  function getRGBA(colorExpr, opacity) {
    let cObj;
    if (colorExpr) cObj = d3.color(colorExpr);
    else cObj = d3.color("#222222");
    if (opacity) cObj.opacity = opacity;
    return "rgba(" + cObj.r + "," + cObj.g + "," + cObj.b + "," + cObj.opacity + ")";
  }
  function decideBlackWhite(colorExpr) {
    let color = getRGBA(colorExpr);
    let rgb = color.replace("rgba(", "").split(/,/g);
    if ((rgb[0] * 0.299 + rgb[1] * 0.587 + rgb[2] * 0.114) > 186) return "#000000"; else return "#ffffff";
  }

  // letter width setup
  const letterWidth = {
    // scaled at font height 12px
    A: 7,
    a: 7,
    B: 8,
    b: 7,
    C: 8,
    c: 6,
    D: 9,
    d: 7,
    E: 7,
    e: 7,
    F: 7,
    f: 4,
    G: 9,
    g: 7,
    H: 9,
    h: 7,
    I: 3,
    i: 3,
    J: 5,
    j: 3,
    K: 8,
    k: 6,
    L: 7,
    l: 3,
    M: 11,
    m: 11,
    N: 9,
    n: 7,
    O: 9,
    o: 7,
    P: 8,
    p: 7,
    Q: 9,
    q: 7,
    R: 8,
    r: 4,
    S: 8,
    s: 6,
    T: 7,
    t: 4,
    U: 9,
    u: 7,
    V: 7,
    v: 6,
    W: 11,
    w: 9,
    X: 7,
    x: 6,
    Y: 7,
    y: 6,
    Z: 7,
    z: 5,
    ".": 2,
    ",": 2,
    ":": 2,
    ";": 2,
    others: 9,
    number: 5,
  };
  function getDefaultBoxWidth(text, height) {
    if (text !== undefined) {
      let textRows = text.split(/\<br\/+\>/g);
      textRows = textRows.map(row => { return row.replace(/\<\/?[^\>]+\>/g, "") });
      let rowWidths = textRows.map((row) => {
        let textSplit = row.replace(/(\<.+\"\>)/g, "").split("");
        return (textSplit.map((d) => (letterWidth[d] ? letterWidth[d] : parseInt(d) || d === "0" ? letterWidth.number : letterWidth.others)).reduce((a, d) => a + d, 0) * height) / 11;
      });
      return d3.max(rowWidths);
    } else {
      return 0;
    }
  }
  // determin if two list are in lineary separable distributions;
  function separatedrange(d1, d2) {
    let minD1 = d3.min(d1),
      maxD1 = d3.max(d1),
      minD2 = d3.min(d2),
      maxD2 = d3.max(d2);
    if (minD1 > maxD2 || maxD1 < minD2) return true;
    else return false;
  }
  // classification: externalized, externalized-numbered, internalized
  let externalized = annoSpec.filter((d) => d.externalize?.type === "yes");
  externalized.forEach((d, i) => {
    if (d.externalize.numbering === "yes") d.externalize.number = getExternalizeNumber(d.externalize?.numberingStyle);
  });
  let exNumbered = externalized.filter((d) => d.externalize.numbering === "yes");
  let internalized = annoSpec.filter((d) => d.externalize === undefined || d.externalize?.type !== "yes");
  // internal annotaions/numberings
  document.querySelectorAll(placeSelector + " [data-role=" + (isAuto ? "auto" : "") + "annotation-callout]").forEach(item => item.remove());
  document.querySelectorAll(placeSelector + " " + (isAuto ? "auto" : "") + "annotation").forEach(item => item.remove());
  if (internalized.length > 0 || exNumbered.length > 0) {
    let annoInside = internalized.concat(exNumbered);
    d3.select(placeSelector + " .chart-wrapper").style("position", "relative");
    let annoLayer = d3.select(placeSelector + " .chart-wrapper"); // directly include to the chart-wrapper to allow for tooltip interaction
    let textOrigin = annoLayer.node().getBoundingClientRect();
    let graphicOrigin = d3
      .select("#view-" + viewname + " svg g")
      .attr("transform")
      .split(/[\)\(,]/g)
      .slice(1, 3)
      .map((d) => +d);
    //resetting
    // pre-processing
    annoInside.forEach((d) => {
      // setting the CSS transform (relative position)
      let transformStates = [];
      if (d.dx) {
        transformStates.push("translateX(" + Fronto.unitString(d.dx, "px") + ")");
      }
      if (d.dy) {
        transformStates.push("translateY(" + Fronto.unitString(d.dy, "px") + ")");
      }
      if (!d.anchor?.dataItem && d.positionOrient) {
        let positionOrient = d.positionOrient ? d.positionOrient.split("-") : ["top", "left"];
        let translateO = [{ "left": 50, "center": 0, "right": -50 }[positionOrient[1]], { "top": 50, "middle": 0, "bottom": -50 }[positionOrient[0]]];
        let translateP = rotateByDeg(translateO, parseFloat(d.rotate) || 0);
        if (translateP[0] - 50 != 0) transformStates.push(`translateX(${translateP[0] - 50}%)`)
        if (translateP[1] - 50 != 0) transformStates.push(`translateY(${translateP[1] - 50}%)`)
      }
      if (d.rotate !== undefined) {
        if (d.positionOrient) d.transformOrigin = d.positionOrient.replace("-", " ");
        else if (d.text?.align === undefined || d.text?.align === "left") d.transformOrigin = "center left";
        else if (d.text?.align === "right") d.transformOrigin = "center right";
        else if (d.text?.align === "center") d.transformOrigin = "center center";

        transformStates.push("rotate(" + Fronto.unitString(d.rotate, "deg") + ")");
      }
      if (d.autoPositioning) {
        let emptySpace = spaceGrid.getLargestContiguousEmptySpace(true);
        d.x = (emptySpace.cellX2 + emptySpace.cellX1) / 2;
        d.y = (emptySpace.cellY2 + emptySpace.cellY1) / 2;
        d.autoPositioning = false;
      }
      if (d.externalize?.numbering === "yes") {
        transformStates.push("translateX(-50%)");
        transformStates.push("translateY(-50%)");
      }
      // setting position when data-anchored
      if (d.anchor?.textAnchor && d.anchor?.dataItem) {
        let anchorOn = getMatchedDatumMark(d.anchor.dataItem, viewname);
        if (!anchorOn) return;
        let innerOrigin = Fronto.getCascadedOrigin(anchorOn, true);
        d.anchor.dataBBox = Fronto.deepcopy(anchorOn.bounds);
        let positionOrient = d.anchor.positionOrient ? d.anchor.positionOrient.split("-") : ["center", "middle"];
        if (positionOrient[1] === "left") d.x = innerOrigin[0] + d.anchor.dataBBox.x1;
        else if (positionOrient[1] === "center") d.x = innerOrigin[0] + (d.anchor.dataBBox.x1 + d.anchor.dataBBox.x2) / 2;
        else if (positionOrient[1] === "right") d.x = innerOrigin[0] + d.anchor.dataBBox.x2;
        if (positionOrient[0] === "top") d.y = innerOrigin[1] + d.anchor.dataBBox.y1;
        else if (positionOrient[0] === "middle") d.y = innerOrigin[1] + (d.anchor.dataBBox.y1 + d.anchor.dataBBox.y2) / 2;
        else if (positionOrient[0] === "bottom") d.y = innerOrigin[1] + d.anchor.dataBBox.y2;
        if (d.text.value.startsWith("<")) {
          let tempDom = document.createElement("div");
          tempDom.innerHTML = d.text.value;
          let dom = tempDom.firstChild;
          if (dom.getAttribute("data-text-color") === "$mark") {
            d.text.color = anchorOn.fill
          } else if (dom.getAttribute("data-text-color") === "$black-white") {
            d.text.color = decideBlackWhite(anchorOn.fill)
          }
          if (dom.getAttribute("data-text-fontstroke") === "$mark") {
            d.text.textShadow = Fronto.makeTextShadow(anchorOn.fill || anchorOn.stroke)
          }
          if (dom.getAttribute("data-text-fontstroke") === "$black-white") {
            d.text.textShadow = Fronto.makeTextShadow(decideBlackWhite(anchorOn.fill) || decideBlackWhite(anchorOn.stroke))
          }
        }
      }
      d.cssTransform = transformStates.join(" ");
    });

    // d3 handling
    let annotationElements = annoLayer.selectAll((isAuto ? "auto" : "") + "annotation").data(annoInside).join("annotation").style("display", "block");
    // make anno/numbering
    annotationElements.attr("data-role", "internalAnnotation");
    // "name": { "type": "string", "fixed": true },
    annotationElements.attr("data-name", (d) => d.name);
    // direct manipulation setting;
    annotationElements.attr("class", (d) => d.className);
    annotationElements.on("dblclick", dblClick);
    // "text>value": { "type": "string" },
    annotationElements.html((d) => (d.externalize?.numbering === "yes" ? d.externalize.number : biFormat(d.text?.value)));
    // "width": { "type": "number" },
    annotationElements.style("box-sizing", "border-box");
    annotationElements.style("width", (d) => {
      if (d.externalize?.numbering === "yes" && !d.width) return "20px";
      else if (+d.width) return Fronto.unitString(d.width, "px");
      else return Fronto.unitString(getDefaultBoxWidth(biEscape(d.text?.value), d.text?.size ? d.text.size : 13), "px");
    });
    // "height": { "type": "number" },
    annotationElements.style("height", (d) => {
      if (d.externalize?.numbering === "yes") return "20px";
      else if (+d.height) return Fronto.unitString(d.height, "px");
      else return "auto";
    });
    // "x": { "type": "number" },
    annotationElements.style("position", "absolute");
    annotationElements.style("left", (d, i, nodes) => {
      if (d.x?.constructor.name === "String" && !d.x.endsWith("px")) return Fronto.unitString(d.x, "px");
      else {
        let x = parseFloat(d.x) || 0;
        let w = parseFloat(d3.select(nodes[i]).style("width"));
        w = +w ? +w : 0;
        if (d.anchor?.anchorAlign === "end") x = x - w;
        if (d.anchor?.anchorAlign === "middle") x = x - w / 2;
        return Fronto.unitString(x, "px");
      }
    });
    // "y": { "type": "number" },
    annotationElements.style("top", (d) => {
      if (d.y?.constructor.name === "String") {
        return Fronto.unitString(d.y, "px");
      }
      else return (+d.y ? +d.y : 0) + "px";
    });
    annotationElements.style("transform", (d) => (d.cssTransform !== undefined ? d.cssTransform : null));
    annotationElements.style("transform-origin", (d) => (d.transformOrigin !== undefined ? d.transformOrigin : null));
    // "opacity": { "type": "number", "helpString": "Range: 0–1" },
    annotationElements.style("opacity", (d) => (d.opacity !== undefined ? d.opacity : 1));
    // "text>size": { "type": "number" },
    annotationElements.style("font-size", (d) => {
      // special for numbering
      if (d.externalize?.numbering === "yes" && !d.box?.borderStyle) return "14px";
      else return d.text?.size !== undefined ? Fronto.unitString(d.text.size, "px") : "14px";
    });
    // "text>lineHeight": { "type": "number", "helpString": "Unit: %" },
    annotationElements.style("line-height", (d) => (d.text?.lineHeight !== undefined ? Fronto.unitString(d.text.lineHeight, "%") : d.externalize?.numbering === "yes" ? "100%" : "120%"));
    // "text>color": { "type": "text"},
    // "text>opacity": { "type": "number" },
    annotationElements.style("color", (d) => {
      return getRGBA(d.text?.color, d.text?.opacity);
    });
    annotationElements.style("text-shadow", (d) => {
      return d.text?.textShadow;
    });
    // "text>align": { "type": ["left", "middle", "right"] },
    annotationElements.style("text-align", (d) => {
      // special for numbering
      if (d.externalize?.numbering === "yes" && !d.box?.align) return "center";
      else return d.text?.align !== undefined ? d.text.align : "left";
    });
    // "text>weight": { "type": [100, 200, 300, 400, 500, 600, 700, 800, 900] },
    annotationElements.style("font-weight", (d) => {
      // special for numbering
      if (d.externalize?.numbering === "yes" && !d.box?.weight) return 700;
      else return d.text?.weight !== undefined ? d.text.weight : 400;
    });
    // X "text>wrap": { "type": ["wrap", "no-wrap"] },
    // "text>overflow": { "type": ["clip", "ellipsis"] },
    annotationElements.style("text-overflow", (d) => (d.text?.overflow !== undefined ? "hidden" : "visible"));
    annotationElements.style("text-overflow", (d) => (d.text?.overflow !== undefined ? d.text.overflow : "ellipsis"));
    // "box>paddingTop": { "type": "string" },
    annotationElements.style("padding-top", (d) => {
      if (d.externalize?.numbering === "yes" && !d.box?.paddingTop) return "3px";
      else if (d.box?.paddingTop !== undefined) return Fronto.unitString(d.box.paddingTop, "px");
      else if (d.box?.padding !== undefined) return Fronto.unitString(d.box.padding, "px");
      else if (d.box?.fill) return "3px";
      else return 0;
    });
    // "box>paddingBottom": { "type": "string" },
    annotationElements.style("padding-bottom", (d) => {
      if (d.externalize?.numbering === "yes" && !d.box?.paddingBottom) return "3px";
      else if (d.box?.paddingBottom !== undefined) return Fronto.unitString(d.box.paddingBottom, "px");
      else if (d.box?.padding !== undefined) return Fronto.unitString(d.box.padding, "px");
      else if (d.box?.fill) return "3px";
      else return 0;
    });
    // "box>paddingLeft": { "type": "string" },
    annotationElements.style("padding-left", (d) => {
      if (d.externalize?.numbering === "yes" && !d.box?.paddingLeft) return "2px";
      else if (d.box?.paddingLeft !== undefined) return Fronto.unitString(d.box.paddingLeft, "px");
      else if (d.box?.padding !== undefined) return Fronto.unitString(d.box.padding, "px");
      else if (d.box?.fill) return "3px";
      else return 0;
    });
    // "box>paddingRight": { "type": "string" },
    annotationElements.style("padding-right", (d) => {
      if (d.externalize?.numbering === "yes" && !d.box?.paddingRight) return "2px";
      else if (d.box?.paddingRight !== undefined) return Fronto.unitString(d.box.paddingRight, "px");
      else if (d.box?.padding !== undefined) return Fronto.unitString(d.box.padding, "px");
      else if (d.box?.fill) return "3px";
      else return 0;
    });
    // "box>rounded": { "type": "number" },
    annotationElements.style("border-radius", (d) => {
      // special for numbering
      return +d.box?.rounded ? Fronto.unitString(d.box.rounded, "px") : 0;
    });
    // "box>fill": { "type": "string" },
    // "box>opacity": { "type": "number" },
    annotationElements.style("background-color", (d) => {
      // if (d.externalize?.numbering === "yes" && !d.box?.fill) return "rgba(255,255,255,1)";
      // else 
      if (!d.box?.fill) return "transparent";
      else return getRGBA(d.box?.fill, d.box?.fillOpacity);
    });
    // "box>borderColor": { "type": "string" },
    // "box>borderOpacity": { "type": "number", "helpString": "Range: 0–1" },
    annotationElements.style("border-color", (d) => {
      return getRGBA(d.box?.borderColor, d.box?.borderOpacity);
    });
    // "box>borderStyle": { "type": ["dotted", "solid", "dashed"] }
    annotationElements.style("border-style", (d) => {
      // special for numbering
      return d.box?.borderStyle !== undefined ? d.box.borderStyle : "solid";
    });
    // "box>borderWidth": { "type": "number" }
    annotationElements.style("border-width", (d) => {
      // if (d.externalize?.numbering === "yes" && !+d.box?.borderWidth) return "2px";
      // else 
      return +d.box?.borderWidth ? Fronto.unitString(d.box.borderWidth, "px") : 0;
    });

    // set data anchoring
    // "anchor>callout": {"type": ["yes", "no"]},
    // "anchor>textX": {"type": "number"},
    // "anchor>textY": {"type": "number"},
    // "anchor>dataX": {"type": "number"},
    // "anchor>dataY": {"type": "number"},

    let inCallouts = annoInside.filter((d) => d.anchor?.callout === "yes");
    inCallouts.forEach((d) => {
      if (d.anchor.dataItem !== undefined) {
        // let padding = d.anchor.padding || 0;
        let strokeColor = d.anchor.strokeColor || "black";
        let strokeWidth = d.anchor.strokeWidth || 1;
        let strokeOpacity = d.anchor.strokeOpacity || 1;
        let strokeDash = d.anchor.strokeDash || null;

        let anchorTo = getMatchedDatumMark(d.anchor.dataItem, viewname);
        d.anchor.dataBBox = Fronto.deepcopy(anchorTo.bounds);
        d.anchor.textBBox = annoLayer
          .select("annotation[data-name=" + d.name + "]")
          .node()
          .getBoundingClientRect();
        d.anchor.textBBox.x -= textOrigin.x;
        d.anchor.textBBox.y -= textOrigin.y;
        let dx1 = d.anchor.dataBBox.x1,
          dx2 = d.anchor.dataBBox.x2,
          dy1 = d.anchor.dataBBox.y1,
          dy2 = d.anchor.dataBBox.y2;
        let dataCenter = [graphicOrigin[0] + (dx1 + dx2) / 2, graphicOrigin[1] + (dy1 + dy2) / 2]
        let position = [
          Math.min(dataCenter[0] + d.anchor.from.dx, dataCenter[0] + d.anchor.to.dx),
          Math.min(dataCenter[1] + d.anchor.from.dy, dataCenter[1] + d.anchor.to.dy)
        ],
          width = Math.abs((parseFloat(d.anchor.from.dx) || 0) - (parseFloat(d.anchor.to.dx) || 0)),
          height = Math.abs((parseFloat(d.anchor.from.dy) || 0) - (parseFloat(d.anchor.to.dy) || 0));

        let calloutObject = annoLayer
          .append("svg")
          .attr("data-role", (isAuto ? "auto" : "") + "annotation-callout")
          .attr("width", Fronto.unitString(width + strokeWidth * 2, "px"))
          .attr("height", Fronto.unitString(height + strokeWidth * 2, "px"))
          .style("position", "absolute")
          .style("left", Fronto.unitString(position[0] - strokeWidth, "px"))
          .style("top", Fronto.unitString(position[1] - strokeWidth, "px"));
        let cornering = d.anchor.cornering?.split("-") || "straight";
        let direction = [];
        if (d.anchor.from.dx < d.anchor.to.dx) direction.push("r")
        else if (d.anchor.from.dx > d.anchor.to.dx) direction.push("l")
        else direction.push("s")
        if (d.anchor.from.dy < d.anchor.to.dy) direction.push("b")
        else if (d.anchor.from.dy > d.anchor.to.dy) direction.push("t")
        else direction.push("s")
        if (cornering !== "straight") {
          let tick = calloutObject
            .append("polyline");
          let points = [[0, 0], [0, 0], [0, 0]]; // data, corner, text
          if (direction[0] === "r") {
            points[0][0] = strokeWidth;
            if (cornering[0] === "left") points[1][0] = strokeWidth;
            if (cornering[0] === "right") points[1][0] = width + strokeWidth;
            points[2][0] = width + strokeWidth;
          } else if (direction[0] === "l") {
            points[0][0] = width + strokeWidth;
            if (cornering[0] === "left") points[1][0] = strokeWidth;
            if (cornering[0] === "right") points[1][0] = width + strokeWidth;
            points[2][0] = strokeWidth;
          } else if (direction[0] === "s") {
            points[0][0] = strokeWidth;
            points[1][0] = strokeWidth;
            points[2][0] = strokeWidth;
          }
          if (direction[1] === "t") {
            points[0][1] = height + strokeWidth;
            if (cornering[1] === "top") points[1][1] = strokeWidth;
            if (cornering[1] === "bottom") points[1][1] = height + strokeWidth;
            points[2][1] = strokeWidth;
          } else if (direction[1] === "b") {
            points[0][1] = strokeWidth;
            if (cornering[1] === "top") points[1][1] = strokeWidth;
            if (cornering[1] === "bottom") points[1][1] = height + strokeWidth;
            points[2][1] = height + strokeWidth;
          } else if (direction[1] === "s") {
            points[0][1] = strokeWidth;
            points[1][1] = strokeWidth;
            points[2][1] = strokeWidth;
          }
          tick.attr("points", points.map((d) => d.join(",")).join(" "))
          tick.attr("fill", "none")
            .attr("stroke", strokeColor)
            .attr("opacity", strokeOpacity)
            .attr("stroke-width", strokeWidth);
          if (strokeDash) tick.attr("stroke-dasharray", strokeDash.join(" "));
        } else {
          let tick = calloutObject
            .append("line");
          if (direction[0] === "r") {
            tick.attr("x1", 0 + strokeWidth)
              .attr("x2", width + strokeWidth)
          } else if (direction[0] === "l") {
            tick.attr("x1", width + strokeWidth)
              .attr("x2", 0 + strokeWidth)
          } else if (direction[0] === "s") {
            tick.attr("x1", 0 + strokeWidth)
              .attr("x2", 0 + strokeWidth)
          }

          if (direction[1] === "t") {
            tick.attr("y1", height + strokeWidth)
              .attr("y2", 0 + strokeWidth)
          } else if (direction[1] === "b") {
            tick.attr("y1", 0 + strokeWidth)
              .attr("y2", height + strokeWidth)
          } else if (direction[1] === "s") {
            tick.attr("y1", 0 + strokeWidth)
              .attr("y2", 0 + strokeWidth)
          }

          tick.attr("stroke", strokeColor)
            .attr("opacity", strokeOpacity)
            .attr("stroke-width", strokeWidth);
          if (strokeDash) tick.attr("stroke-dasharray", strokeDash.join(" "));
        }
      }
    });

  }
  // Externalized annotations (with numbering)
  if (externalized.length > 0) {
    // classification: position of exteranlized annotations
    let exTop = externalized.filter((d) => d.externalize?.position === "top");
    let exBottom = externalized.filter((d) => d.externalize?.position === "bottom" || d.externalize?.position === undefined);
    let exLeft = externalized.filter((d) => d.externalize?.position === "left");
    let exRight = externalized.filter((d) => d.externalize?.position === "right");
    let defaultWidth = 300;
    if (exTop.length > 0) {
      let wrapperWidth = document.querySelector(placeSelector + " .chart-wrapper").getBoundingClientRect().width;
      // top-positioned (comes after titles)
      let annoArea;
      if (d3.select(placeSelector + " div[data-role=fronto-title]").nodes().length > 0) {
        annoArea = d3.select(placeSelector).insert("div", "div[data-role=fronto-title] + div");
      }
      else annoArea = d3.select(placeSelector).insert("div", ":first-child"); // set the area and default layout
      annoArea.style("padding-bottom", "15px");
      annoArea.style("position", "relative");
      annoArea.style("font-size", "14px");
      annoArea.style("width", Fronto.unitString(wrapperWidth, "px"));
      let chart = d3.select(placeSelector + " .chart-wrapper");
      chart.style("height", "auto");
      let parent = d3.select(d3.select(placeSelector).node().parentElement);
      d3.select(placeSelector).style("height", "auto");
      parent.style("height", "auto");
      let annotationElements = annoArea.selectAll("div").data(exTop).join("div").style("display", "flex"); // make the annotations
      annotationElements.html((d) => {
        if (d.externalize.numbering === "yes") return "<strong style=\"padding-right: 5px;\">" + d.externalize.number + ".</strong> " + `<div>` + d.text?.value + `</div>`;
        else if (d.externalize.numbering === "hidden") return "<strong style=\"padding-right: 5px;\">" + d.externalize.number_count + ".</strong> " + `<div>` + d.text?.value + `</div>`;
        else return `<div>` + d.text?.value + `</div>`;
      });
      annotationElements.attr("class", (d) => d.className);
      annotationElements.attr("data-name", (d) => d.name);
      annotationElements.on("dblclick", dblClick);
      annotationElements.attr("data-role", "externalAnnotation");
    }
    if (exBottom.length > 0) {
      let wrapperWidth = document.querySelector(placeSelector + " .chart-wrapper").getBoundingClientRect().width;
      // bottom positioned
      let annoArea = d3.select(placeSelector).insert("div", ".chart-wrapper + div");
      annoArea.style("margin-top", "15px");
      annoArea.style("position", "relative");
      annoArea.style("font-size", "14px");
      annoArea.style("width", Fronto.unitString(wrapperWidth, "px"));
      let chart = d3.select(placeSelector + " .chart-wrapper");
      chart.style("height", "auto");
      let parent = d3.select(d3.select(placeSelector).node().parentElement);
      d3.select(placeSelector).style("height", "auto");
      parent.style("height", "auto");
      let annotationElements = annoArea.selectAll("div").data(exBottom).join("div").style("display", "flex");
      annotationElements.html((d) => {
        if (d.externalize.numbering === "yes") return "<strong style=\"padding-right: 5px;\">" + d.externalize.number + ".</strong> " + `<div>` + d.text?.value + `</div>`;
        else if (d.externalize.numbering === "hidden") return "<strong style=\"padding-right: 5px;\">" + d.externalize.number_count + ".</strong> " + `<div>` + d.text?.value + `</div>`;
        else return `<div>` + d.text?.value + `</div>`;
      });
      annotationElements.attr("class", (d) => d.className);
      annotationElements.attr("data-name", (d) => d.name);
      annotationElements.attr("data-role", "externalAnnotation");
      annotationElements.on("dblclick", dblClick);
      annotationElements.style("margin-top", "15px");
    }
    if (exLeft.length > 0) {
      // left positioned (flex view)
      let annoArea = d3.select(placeSelector).insert("div", ":first-child"); // set the flex box layout
      annoArea.style("width", Fronto.unitString(defaultWidth, "px"));
      annoArea.style("padding-right", "15px");
      annoArea.style("font-size", "14px");
      d3.select(placeSelector).style("display", "flex");
      let chartBox = d3.select(placeSelector + " .chart-wrapper");
      let chart = d3.select(placeSelector + " .chart-wrapper svg");
      let chartBBox = chart.node().getBoundingClientRect();
      chartBox.style("width", Fronto.unitString(chartBBox.width, "px"));
      let newWidth = parseInt(d3.select(placeSelector).style("width")) + defaultWidth;
      d3.select(placeSelector).style("width", Fronto.unitString(newWidth, "px"));
      let parent = d3.select(d3.select(placeSelector).node().parentElement);
      let newWidth2 = parseInt(parent.style("width")) + defaultWidth;
      parent.style("width", Fronto.unitString(newWidth2, "px"));
      let annotationElements = annoArea.selectAll("div").data(exLeft).join("div"); // make annotations
      annotationElements.html((d) => {
        if (d.externalize.numbering === "yes") return "<strong>" + d.externalize.number + ".</strong> " + `<div>` + d.text?.value + `</div>`;
        else if (d.externalize.numbering === "hidden") return "<strong>" + d.externalize.number_count + ".</strong> " + `<div>` + d.text?.value + `</div>`;
        else return `<div>` + d.text?.value + `</div>`;
      });
      annotationElements.attr("class", (d) => d.className);
      annotationElements.attr("data-name", (d) => d.name);
      annotationElements.attr("data-role", "externalAnnotation");
      annotationElements.on("dblclick", dblClick);
    }
    if (exRight.length > 0) {
      // right positioned (flex view)
      let annoArea = d3.select(placeSelector).insert("div", ".chart-wrapper + div");
      annoArea.style("width", Fronto.unitString(defaultWidth, "px"));
      annoArea.style("padding-left", "15px");
      annoArea.style("font-size", "14px");
      d3.select(placeSelector).style("display", "flex");
      let chartBox = d3.select(placeSelector + " .chart-wrapper");
      let chart = d3.select(placeSelector + " .chart-wrapper svg");
      let chartBBox = chart.node().getBoundingClientRect();
      chartBox.style("width", Fronto.unitString(chartBBox.width, "px"));
      let newWidth = parseInt(d3.select(placeSelector).style("width")) + defaultWidth;
      d3.select(placeSelector).style("width", Fronto.unitString(newWidth, "px"));
      let parent = d3.select(d3.select(placeSelector).node().parentElement);
      let newWidth2 = parseInt(parent.style("width")) + defaultWidth;
      parent.style("width", Fronto.unitString(newWidth2, "px"));
      let annotationElements = annoArea.selectAll("div").data(exRight).join("div");
      annotationElements.html((d) => {
        if (d.externalize.numbering === "yes") return "<strong>" + d.externalize.number + ".</strong> " + `<div>` + d.text?.value + `</div>`;
        else if (d.externalize.numbering === "hidden") return "<strong>" + d.externalize.number_count + ".</strong> " + `<div>` + d.text?.value + `</div>`;
        else return `<div>` + d.text?.value + `</div>`;
      });
      annotationElements.attr("class", (d) => d.className);
      annotationElements.attr("data-name", (d) => d.name);
      annotationElements.attr("data-role", "externalAnnotation");
      annotationElements.on("dblclick", dblClick);
    }
  }
}


function rotateByDeg(coord, deg) {
  let x = coord[0], y = coord[1], rad = deg / 360 * (Math.PI * 2);
  return [x * Math.cos(rad) + y * Math.sin(rad), - x * Math.sin(rad) + y * Math.cos(rad)];
}